<script lang="ts">
  import { DEFAULT_ORGANIZATION_TIMEZONE } from "../../../../../../../../../../../_shared/_logic/timezones"
  import { onMount } from "svelte"
  import { fetchPlanningSettings } from "../../../../../../../../../../../../api/kehrblatt/planning"
  import type {
    InboxWorkflowCommitResult,
    InboxWorkflowCustomStepProps,
    InboxWorkflowRowControls,
  } from "@kontextwerk/control-web-frame/inbox-workflow/contract"
  import { userDisplayName } from "@kontextwerk/control-web-frame/logic/userDisplay"
  import AppointmentPlanningSurface from "../../../../../../../../../../_shared/_modules/appointment-planning-surface"
  import type {
    AppointmentPlanningExistingSourceRow,
    AppointmentPlanningOperations,
    AppointmentPlanningSettingsSnapshot,
    AppointmentPlanningSubmissionResult,
  } from "../../../../../../../../../../_shared/_logic/appointments-contract"
  import { buildWorkingHoursByUser } from "../../../../../../../../../../../../_shared/_logic/working-hours-view"
  import { filterActiveOrganizationUsers } from "../../../../../../../../../../_shared/_logic/organization-users"
  import type {
    AppointmentPlanningCommitPayload,
    AppointmentPlanningPreviewPayload,
    AppointmentPlanningPreviewResponse,
  } from "../../../../../../../../../../../../api/kehrblatt/planning"
  import type { InboxCaseBatchWorkflow } from "../../../../../../../../../../../../api/kehrblatt/inbox-cases"
  import { buildInboxCustomStepRowPresentations } from "../../../../_shared/_logic/custom-step-rows"
  import {
    buildSourceRows,
    numberValue,
    planSourceRowErrors,
    planningSettings,
    sourceStartDate,
    stringValue,
  } from "./_logic/sourceRows"
  import { buildInitialPlannedRows, buildPlanningResponseFromInboxResult } from "./_logic/outputs"
  import { buildCommitItems, planningPreviewPayload } from "./_logic/payload"
  import type { PlanAppointmentSourcePreview } from "./contract"
  import { buildPlanningResponseForSurface } from "./_logic/planningResponse"
  import { parsePlanAppointmentPreview } from "./_logic/preview"

  type PlanningUser = NonNullable<InboxWorkflowCustomStepProps["organization"]>["users"][number]

  let props: InboxWorkflowCustomStepProps = $props()

  const STEP_KEY = "plan_appointment"

  const workflow = $derived(props.workflow as unknown as InboxCaseBatchWorkflow)
  const rowControls = $derived(
    (props.extra?.rowControls ?? null) as InboxWorkflowRowControls | null,
  )
  const rowPresentations = $derived(
    buildInboxCustomStepRowPresentations({
      workflow: props.workflow,
      step: props.step,
      rowOutcomes: props.rowOutcomes,
      rowControls,
    }),
  )
  const organizationId = $derived(
    stringValue(
      props.organizationId,
      props.organization?.id,
      workflow.items[0]?.action_case.organization_id,
    ),
  )
  const activeUsers = $derived(
    filterActiveOrganizationUsers((props.organization?.users ?? []) as PlanningUser[]),
  )
  const employeeOptions = $derived(
    activeUsers.map((user) => ({
      label: userDisplayName(user) || "Unbekannt",
      value: String(user.id),
    })),
  )
  const workingHoursByUser = $derived(buildWorkingHoursByUser(activeUsers))
  let sourceContextPreview = $state<PlanAppointmentSourcePreview | null>(null)
  let sourceContextLoading = $state(true)
  let sourceContextError = $state<string | null>(null)
  let planningTimezone = $state(DEFAULT_ORGANIZATION_TIMEZONE)
  const settings = $derived(planningSettings(sourceContextPreview))
  const flexSlotsPerDay = $derived(numberValue(settings.flex_slots_per_day) ?? 0)
  const sourceRows = $derived(
    organizationId && sourceContextPreview ? buildSourceRows(workflow, sourceContextPreview) : [],
  )
  const sourceRowErrors = $derived.by(() => {
    if (sourceContextLoading || sourceContextError) return []

    return planSourceRowErrors(workflow, sourceContextPreview)
  })
  const sourceSurfaceError = $derived.by(() => {
    if (sourceContextLoading) return null

    if (sourceContextError) return sourceContextError

    if (!sourceRows.length && !sourceRowErrors.length)
      return "Für diesen Schritt sind keine planbaren Aufträge vorhanden."

    if (!sourceRowErrors.length) return null

    const messages = Array.from(new Set(sourceRowErrors.map((error) => error.message)))

    return `Backend-Pflichtdaten fehlen: ${messages.join(" ")}`
  })
  const persistedOutputRevision = $derived(
    workflow.items
      .map((item) => {
        const persistedStep = item.steps.find((candidate) => candidate.step_key === STEP_KEY)

        return `${item.action_case.id}:${persistedStep?.status ?? ""}:${JSON.stringify(persistedStep?.output ?? {})}`
      })
      .join("|"),
  )
  const sourceSurfaceKey = $derived(
    sourceContextLoading
      ? "loading"
      : sourceContextPreview
        ? `ready:${persistedOutputRevision}`
        : "error",
  )
  const hiddenActionCaseIds = $derived(
    Object.values(rowPresentations)
      .filter((presentation) => presentation.removedEarlier)
      .map((presentation) => presentation.actionCaseId),
  )
  const initialRemovedRowIds = $derived(
    Object.values(rowPresentations)
      .filter((presentation) => presentation.removedCurrent)
      .map((presentation) => presentation.actionCaseId),
  )
  const durableReadOnlyRowIds = $derived(
    Object.values(rowPresentations)
      .filter((presentation) => presentation.disabled && !presentation.removedCurrent)
      .map((presentation) => presentation.actionCaseId),
  )
  const rowNumberById = $derived(
    Object.fromEntries(
      Object.values(rowPresentations).map((presentation) => [
        presentation.actionCaseId,
        presentation.rowNumber,
      ]),
    ),
  )
  const hiddenActionCaseIdSet = $derived(new Set(hiddenActionCaseIds))
  const activeSourceRows = $derived(sourceRows.filter((row) => !hiddenActionCaseIdSet.has(row.id)))
  const initialPlannedRows = $derived(
    organizationId ? buildInitialPlannedRows({ workflow, sourceRows, organizationId }) : [],
  )
  const defaultStartDate = $derived(sourceStartDate(activeSourceRows))
  let surfaceErrorMessage = $state<string | null>(null)
  const initialSettings = $derived({
    workOrderOverridesByRowId: {},
    startDate: defaultStartDate,
    selectedEmployees: employeeOptions.map((option) => option.value),
    planFlexAppointments: flexSlotsPerDay > 0,
  } satisfies AppointmentPlanningSettingsSnapshot)

  onMount(() => {
    if (!organizationId) {
      sourceContextLoading = false

      return
    }

    let active = true
    const actionCaseIds = workflow.items
      .filter((item) => item.compatible)
      .map((item) => item.action_case.id)

    if (!props.loadPreview) {
      sourceContextLoading = false
      sourceContextError = "Preview-Adapter fehlt."

      return
    }

    void Promise.all([
      props.loadPreview({
        action_case_ids: actionCaseIds,
        payload: { mode: "source" },
      }),
      fetchPlanningSettings(organizationId),
    ])
      .then(([result, planningSettings]) => {
        if (!active) return

        if (
          result.flow_key !== workflow.flow.flow_key ||
          result.step_key !== STEP_KEY ||
          result.preview_kind !== "unclear_appointment_feedback.plan_appointment.source"
        ) {
          throw new Error("Terminplanungs-Source-Preview passt nicht zum angeforderten Step.")
        }

        const parsed = parsePlanAppointmentPreview(result.payload)

        if (parsed.mode !== "source") {
          throw new Error("Terminplanungs-Source-Preview hat den falschen Modus.")
        }

        sourceContextPreview = parsed
        planningTimezone = planningSettings.timezone ?? DEFAULT_ORGANIZATION_TIMEZONE
        sourceContextError = null
      })
      .catch((error) => {
        if (!active) return

        console.error("appointment_planning_source_preview_failed", error)
        sourceContextPreview = null
        sourceContextError = "Terminplanungsdaten konnten nicht geladen werden."
      })
      .finally(() => {
        if (!active) return

        sourceContextLoading = false
      })

    return () => {
      active = false
    }
  })

  const previewAppointmentBatch = async (
    payload: AppointmentPlanningPreviewPayload,
    signal?: AbortSignal,
  ): Promise<AppointmentPlanningPreviewResponse> => {
    if (!organizationId) throw new Error("Organisation fehlt.")

    const actionCaseIds = actionCaseIdsForPlanningPayload(payload, sourceRows)

    if (!props.loadPreview) throw new Error("Preview-Adapter fehlt.")

    const result = await props.loadPreview(
      {
        action_case_ids: actionCaseIds,
        payload: planningPreviewPayload(payload, sourceRows),
      },
      signal,
    )

    if (
      result.flow_key !== workflow.flow.flow_key ||
      result.step_key !== STEP_KEY ||
      result.preview_kind !== "unclear_appointment_feedback.plan_appointment.planning"
    ) {
      throw new Error("Terminplanungs-Vorschau passt nicht zum angeforderten Step.")
    }

    const parsed = parsePlanAppointmentPreview(result.payload)

    if (parsed.mode !== "planning")
      throw new Error("Terminplanungs-Vorschau hat den falschen Modus.")

    return buildPlanningResponseForSurface({
      preview: parsed,
      requestedActionCaseIds: actionCaseIds,
    })
  }

  const actionCaseIdsForPlanningPayload = (
    payload: AppointmentPlanningPreviewPayload,
    rows: AppointmentPlanningExistingSourceRow[],
  ): string[] => {
    const sourceRowIds = new Set(rows.map((row) => row.id))

    return Array.from(
      new Set(
        payload.rows
          .map((row) => stringValue(row.client_row_id))
          .filter((rowId) => rowId && sourceRowIds.has(rowId)),
      ),
    )
  }

  const handleRowsRemoved = (rowIds: string[]) => {
    rowControls?.removeActionCasesFromCurrentStep(rowIds)
  }

  const handleRowsRestored = (rowIds: string[]) => {
    rowControls?.restoreActionCasesForCurrentStep(rowIds)
  }

  const commitAppointmentBatch = async (payload: AppointmentPlanningCommitPayload) => {
    if (!organizationId) throw new Error("Organisation fehlt.")

    const receipt = await props.commit(buildCommitItems({ sourceRows, payload }))

    return {
      response: buildPlanningResponseFromInboxResult({
        workflow,
        result: receipt,
        sourceRows,
        organizationId,
      }),
      receipt,
    }
  }

  const operations = $derived({
    loadPreview: previewAppointmentBatch,
    commit: commitAppointmentBatch,
  } satisfies AppointmentPlanningOperations<InboxWorkflowCommitResult>)

  const handleSubmissionResult = async (
    submission: AppointmentPlanningSubmissionResult<InboxWorkflowCommitResult>,
  ) => {
    if (submission.kind === "committed") await props.complete(submission.receipt)
  }
</script>

{#if !organizationId}
  <p class="error-message">Organisation fehlt. Der Planungsschritt kann nicht geladen werden.</p>
{:else}
  {#if surfaceErrorMessage}
    <p class="error-message">{surfaceErrorMessage}</p>
  {/if}
  {#key sourceSurfaceKey}
    <AppointmentPlanningSurface
      {organizationId}
      trialRestrictionsActive={props.trialRestrictionsActive}
      existingWorkOrderRows={sourceRows}
      initialExistingPlannedRows={initialPlannedRows}
      hiddenRowIds={hiddenActionCaseIds}
      {initialRemovedRowIds}
      {durableReadOnlyRowIds}
      {rowNumberById}
      {initialSettings}
      {employeeOptions}
      {workingHoursByUser}
      {flexSlotsPerDay}
      {defaultStartDate}
      defaultStartPreset="custom"
      settingsTimezone={planningTimezone}
      {operations}
      sourceLoading={sourceContextLoading}
      sourceError={sourceSurfaceError}
      existingSubmitLabel="Termine planen"
      tableTitle="Terminplanung"
      tableDescription="Planen Sie den Zieltermin fuer die ausgewaehlten Auftraege."
      helpModalTextId={props.help?.textId ?? ""}
      readOnly={props.readOnly}
      onSubmissionResult={handleSubmissionResult}
      onRowsRemoved={handleRowsRemoved}
      onRowsRestored={handleRowsRestored}
      setError={(message) => {
        surfaceErrorMessage = message
      }}
    />
  {/key}
{/if}

<style>
  .error-message {
    margin: 1rem 0;
    color: #b91c1c;
    font-weight: 700;
  }
</style>
